import React, { useState, useCallback } from 'react';
import { useAuth } from '../../contexts/Auth/authContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useI18n } from '../../contexts/I18nContext';
import { apiClient } from '../../services/apiClient';
import { apiStatsService } from '../../services/apiStatsService';
import config from '../../config/env';
import { safeStorage } from '../../utils/safeStorage';
import './SystemValidationTests.css';

interface TestResult {
  name: string;
  status: 'pending' | 'running' | 'passed' | 'failed' | 'warning';
  message: string;
  details?: any;
  duration?: number;
}

interface SystemValidationTestsProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SystemValidationTests: React.FC<SystemValidationTestsProps> = ({ isOpen, onClose }) => {
  const { isAuthenticated } = useAuth();
  const { theme, isDark } = useTheme();
  const { t, language, formatDateTime } = useI18n();
  const [isRunning, setIsRunning] = useState(false);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [overallStatus, setOverallStatus] = useState<'idle' | 'running' | 'completed'>('idle');

  // Define all tests
  const runSystemTests = useCallback(async () => {
    setIsRunning(true);
    setOverallStatus('running');
    setTestResults([]);

    const tests: (() => Promise<TestResult>)[] = [
      // Authentication Tests
      async () => {
        const start = Date.now();
        try {
          const token = safeStorage.getItem('access_token');
          const user = safeStorage.getItem('user');

          if (!token || !isAuthenticated) {
            return {
              name: t('debug.authenticationTest'),
              status: 'failed',
              message: t('errors.authenticationError'),
              duration: Date.now() - start
            };
          }

          // Try to decode JWT (basic validation)
          const payload = JSON.parse(atob(token.split('.')[1]));
          const isExpired = payload.exp * 1000 < Date.now();

          return {
            name: t('debug.authenticationTest'),
            status: isExpired ? 'warning' : 'passed',
            message: isExpired ? t('auth.sessionExpired') : t('auth.signedInAs') + ` ${user || 'User'}`,
            details: { tokenExpiry: formatDateTime(payload.exp * 1000) },
            duration: Date.now() - start
          };
        } catch (error) {
          return {
            name: t('debug.authenticationTest'),
            status: 'failed',
            message: t('errors.authenticationError'),
            details: error,
            duration: Date.now() - start
          };
        }
      },

      // API Health Test
      async () => {
        const start = Date.now();
        try {
          const response = await fetch('/api/health', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          });

          return {
            name: t('debug.apiHealthTest'),
            status: response.ok ? 'passed' : 'failed',
            message: response.ok ? t('debug.apiHealthy') : t('debug.apiUnhealthy'),
            details: { status: response.status, statusText: response.statusText },
            duration: Date.now() - start
          };
        } catch (error) {
          return {
            name: t('debug.apiHealthTest'),
            status: 'failed',
            message: t('errors.networkError'),
            details: error,
            duration: Date.now() - start
          };
        }
      },

      // Device API Test
      async () => {
        const start = Date.now();
        try {
          const response = await apiClient.getAxiosInstance().get('/v2/conf');
          const devices = Array.isArray(response.data) ? response.data : [];

          return {
            name: t('debug.deviceApiTest'),
            status: 'passed',
            message: t('debug.devicesLoaded', { count: devices.length }),
            details: { deviceCount: devices.length, sampleDevice: devices[0] || null },
            duration: Date.now() - start
          };
        } catch (error: any) {
          return {
            name: t('debug.deviceApiTest'),
            status: 'failed',
            message: t('debug.deviceApiError'),
            details: { error: error.message, status: error.response?.status },
            duration: Date.now() - start
          };
        }
      },

      // OAuth SSO Test
      async () => {
        const start = Date.now();
        try {
          const ssoUrl = config.oauth.authUrl;
          const response = await fetch(ssoUrl.replace('/auth', '/.well-known/openid_configuration'), {
            method: 'GET'
          });

          return {
            name: t('debug.ssoConnectivityTest'),
            status: response.ok ? 'passed' : 'warning',
            message: response.ok ? t('debug.ssoReachable') : t('debug.ssoUnreachable'),
            details: { status: response.status, url: ssoUrl },
            duration: Date.now() - start
          };
        } catch (error) {
          return {
            name: t('debug.ssoConnectivityTest'),
            status: 'warning',
            message: t('debug.ssoUnreachable'),
            details: error,
            duration: Date.now() - start
          };
        }
      },

      // Theme System Test
      async () => {
        const start = Date.now();
        try {
          const themeData = {
            currentTheme: isDark ? 'dark' : 'light',
            themeMode: theme.mode,
            variant: theme.variant,
            animations: theme.animations,
            cssVariablesCount: document.documentElement.style.length,
            dataAttribute: document.documentElement.getAttribute('data-theme')
          };

          return {
            name: t('debug.themeSystemTest'),
            status: 'passed',
            message: t('debug.themeWorking'),
            details: themeData,
            duration: Date.now() - start
          };
        } catch (error) {
          return {
            name: t('debug.themeSystemTest'),
            status: 'failed',
            message: t('debug.themeError'),
            details: error,
            duration: Date.now() - start
          };
        }
      },

      // Internationalization Test
      async () => {
        const start = Date.now();
        try {
          const sample = t('common.loading');
          const i18nData = {
            currentLanguage: language,
            // If translations failed to load, t() returns the key string
            // unchanged — so a value that differs from the key is proof
            // the locale resolved.
            translationsLoaded: typeof sample === 'string' && sample !== 'common.loading',
            sampleTranslation: sample,
            browserLanguage: navigator.language
          };

          return {
            name: t('debug.i18nTest'),
            status: i18nData.translationsLoaded ? 'passed' : 'failed',
            message: i18nData.translationsLoaded ? t('debug.i18nWorking') : t('debug.i18nError'),
            details: i18nData,
            duration: Date.now() - start
          };
        } catch (error) {
          return {
            name: t('debug.i18nTest'),
            status: 'failed',
            message: t('debug.i18nError'),
            details: error,
            duration: Date.now() - start
          };
        }
      },

      // API Statistics Test
      async () => {
        const start = Date.now();
        try {
          const stats = apiStatsService.getStats();
          const statsData = {
            totalRequests: stats.totalRequests,
            historyLength: stats.requestHistory.length,
            successRate: stats.totalRequests > 0 ? Math.round((stats.successfulRequests / stats.totalRequests) * 100) : 0,
            averageResponseTime: apiStatsService.getAverageResponseTime()
          };

          return {
            name: t('debug.apiStatsTest'),
            status: 'passed',
            message: t('debug.apiStatsWorking'),
            details: statsData,
            duration: Date.now() - start
          };
        } catch (error) {
          return {
            name: t('debug.apiStatsTest'),
            status: 'failed',
            message: t('debug.apiStatsError'),
            details: error,
            duration: Date.now() - start
          };
        }
      },

      // Local Storage Test
      async () => {
        const start = Date.now();
        try {
          const testKey = 'debug_test_' + Date.now();
          const testValue = 'test_value';

          safeStorage.setItem(testKey, testValue);
          const retrieved = safeStorage.getItem(testKey);
          safeStorage.removeItem(testKey);

          const storageData = {
            working: retrieved === testValue,
            totalItems: localStorage.length,
            hasThemeConfig: !!localStorage.getItem('theme-config'),
            hasApiStats: !!localStorage.getItem('api_stats'),
            hasLanguageConfig: !!localStorage.getItem('preferred-language')
          };

          return {
            name: t('debug.localStorageTest'),
            status: storageData.working ? 'passed' : 'failed',
            message: storageData.working ? t('debug.localStorageWorking') : t('debug.localStorageError'),
            details: storageData,
            duration: Date.now() - start
          };
        } catch (error) {
          return {
            name: t('debug.localStorageTest'),
            status: 'failed',
            message: t('debug.localStorageError'),
            details: error,
            duration: Date.now() - start
          };
        }
      },

      // Environment Configuration Test
      async () => {
        const start = Date.now();
        try {
          const envData = {
            debugMode: config.dev.debug,
            apiBaseUrl: config.api.baseUrl,
            oauthClientId: !!config.oauth.clientId,
            oauthRedirectUri: !!config.oauth.redirectUri,
            nodeEnv: process.env.NODE_ENV,
            reactVersion: React.version
          };

          const hasRequiredEnv = envData.oauthClientId && envData.oauthRedirectUri;

          return {
            name: t('debug.envConfigTest'),
            status: hasRequiredEnv ? 'passed' : 'warning',
            message: hasRequiredEnv ? t('debug.envConfigOk') : t('debug.envConfigMissing'),
            details: envData,
            duration: Date.now() - start
          };
        } catch (error) {
          return {
            name: t('debug.envConfigTest'),
            status: 'failed',
            message: t('debug.envConfigError'),
            details: error,
            duration: Date.now() - start
          };
        }
      }
    ];

    // Run tests sequentially with visual updates
    const results: TestResult[] = [];
    for (let i = 0; i < tests.length; i++) {
      const testResult = await tests[i]();
      results.push(testResult);
      setTestResults([...results]);

      // Small delay for visual effect
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    setIsRunning(false);
    setOverallStatus('completed');

    if (config.dev.debug) {
      console.log('[DEBUG TESTS] All tests completed:', results);
    }
  }, [isAuthenticated, theme, isDark, language, t, formatDateTime]);

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'passed': return '✅';
      case 'failed': return '❌';
      case 'warning': return '⚠️';
      case 'running': return '⏳';
      default: return '⏸️';
    }
  };

  const getStatusColor = (status: TestResult['status']) => {
    switch (status) {
      case 'passed': return 'var(--success-color, #28a745)';
      case 'failed': return 'var(--error-color, #dc3545)';
      case 'warning': return 'var(--warning-color, #ffc107)';
      case 'running': return 'var(--info-color, #17a2b8)';
      default: return 'var(--text-muted, #6c757d)';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="validation-tests-modal-overlay" onClick={onClose}>
      <div className="validation-tests-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="validation-tests-header">
          <h2>🧪 {t('debug.systemValidation')}</h2>
          <button className="validation-tests-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>

        <div className="validation-tests-body">
          <div className="validation-tests-controls">
            <button
              className="validation-test-run-btn"
              onClick={runSystemTests}
              disabled={isRunning}
            >
              {isRunning ? t('common.loading') : t('debug.runTests')}
            </button>

            {overallStatus === 'completed' && (
              <div className="validation-overall-status">
                <span className="validation-overall-label">{t('debug.overallStatus')}:</span>
                <span className="validation-overall-value">
                  {testResults.filter(r => r.status === 'passed').length}/{testResults.length} {t('debug.testsPassed')}
                </span>
              </div>
            )}
          </div>

          <div className="validation-tests-results">
            {testResults.length === 0 && !isRunning && (
              <div className="validation-no-tests">
                <p>{t('debug.noTestsRun')}</p>
                <small>{t('debug.clickRunTests')}</small>
              </div>
            )}

            {testResults.map((result, index) => (
              <div key={index} className={`validation-test-result ${result.status}`}>
                <div className="validation-test-header">
                  <span className="validation-test-icon">{getStatusIcon(result.status)}</span>
                  <span className="validation-test-name">{result.name}</span>
                  <span className="validation-test-duration">
                    {result.duration ? `${result.duration}ms` : ''}
                  </span>
                </div>
                <div className="validation-test-message" style={{ color: getStatusColor(result.status) }}>
                  {result.message}
                </div>
                {result.details && (
                  <details className="validation-test-details">
                    <summary>{t('debug.testDetails')}</summary>
                    <pre>{JSON.stringify(result.details, null, 2)}</pre>
                  </details>
                )}
              </div>
            ))}

            {isRunning && (
              <div className="validation-running-indicator">
                <div className="validation-spinner"></div>
                <span>{t('debug.runningTests')}</span>
              </div>
            )}
          </div>
        </div>

        <div className="validation-tests-footer">
          <div className="validation-tests-info">
            <small>
              {t('debug.testsDescription')} •
              {t('debug.debugModeOnly')}: {config.dev.debug ? t('theme.on') : t('theme.off')}
            </small>
          </div>
          <button className="validation-tests-close-btn" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};